import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  padding: 100px 20px 50px;
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Title = styled.h1`
  font-size: 3rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: #a0aec0;
  font-size: 1.2rem;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
`;

const CardTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  margin-bottom: 20px;
`;

const LoadingText = styled.p`
  color: #a0aec0;
  text-align: center;
  font-size: 1.1rem;
`;

const ErrorText = styled.p`
  color: #fc8181;
  text-align: center;
  font-size: 1.1rem;
`;

const Button = styled(motion.button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin: 10px 5px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white;

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  color: #a0aec0;
  font-weight: 500;
`;

const Value = styled.span`
  color: white;
  font-weight: 600;
`;

const RecommendationsList = styled.div`
  margin-top: 20px;
`;

const RecommendationItem = styled.div<{ type: string }>`
  background: ${props => {
    switch (props.type) {
      case 'critical': return 'rgba(252, 129, 129, 0.1)';
      case 'high': return 'rgba(246, 173, 85, 0.1)';
      case 'medium': return 'rgba(72, 187, 120, 0.1)';
      default: return 'rgba(255, 255, 255, 0.05)';
    }
  }};
  border-left: 4px solid ${props => {
    switch (props.type) {
      case 'critical': return '#fc8181';
      case 'high': return '#f6ad55';
      case 'medium': return '#48bb78';
      default: return '#667eea';
    }
  }};
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 8px;
`;

const RecommendationType = styled.div`
  color: ${props => {
    switch (props.color) {
      case 'critical': return '#fc8181';
      case 'high': return '#f6ad55';
      case 'medium': return '#48bb78';
      default: return '#667eea';
    }
  }};
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.8rem;
  margin-bottom: 5px;
`;

const RecommendationMessage = styled.div`
  color: white;
  font-weight: 600;
  margin-bottom: 5px;
`;

const RecommendationAction = styled.div`
  color: #a0aec0;
  font-size: 0.9rem;
`;

const ScoreCircle = styled.div<{ score: number }>`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: conic-gradient(
    #667eea 0deg ${props => props.score * 3.6}deg,
    rgba(255, 255, 255, 0.1) ${props => props.score * 3.6}deg 360deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: #1a1a2e;
  }
`;

const ScoreText = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
  font-size: 2rem;
  font-weight: 700;
`;

interface Account {
  name: string;
  accountName: string;
  type: string;
}

interface Location {
  name: string;
  title: string;
  storefrontAddress?: any;
  phoneNumbers?: any[];
  websiteUri?: string;
}

interface Recommendation {
  type: string;
  field: string;
  message: string;
  action: string;
}

const GoogleBusinessManager: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [locationDetails, setLocationDetails] = useState<any>(null);
  const [optimization, setOptimization] = useState<any>(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('http://localhost:3001/api/google-business/accounts');
      
      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }
      
      const data = await response.json();
      
      if (data.accounts && data.accounts.length > 0) {
        setAccounts(data.accounts);
        // Auto-select first account
        const firstAccount = data.accounts[0].name;
        setSelectedAccount(firstAccount);
        await fetchLocations(firstAccount);
      } else {
        setError('No Google Business accounts found. Make sure the service account has been added as a manager.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async (accountName: string) => {
    try {
      setLoading(true);
      const encodedAccount = encodeURIComponent(accountName);
      const response = await fetch(`http://localhost:3001/api/google-business/locations/${encodedAccount}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch locations');
      }
      
      const data = await response.json();
      
      if (data.locations && data.locations.length > 0) {
        setLocations(data.locations);
        // Auto-select first location
        const firstLocation = data.locations[0].name;
        setSelectedLocation(firstLocation);
        await fetchLocationDetails(firstLocation);
        await fetchOptimization(firstLocation);
      } else {
        setError('No locations found for this account');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load locations');
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationDetails = async (locationName: string) => {
    try {
      const encodedLocation = encodeURIComponent(locationName);
      const response = await fetch(`http://localhost:3001/api/google-business/location/${encodedLocation}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch location details');
      }
      
      const data = await response.json();
      setLocationDetails(data);
    } catch (err: any) {
      console.error('Error fetching location details:', err);
    }
  };

  const fetchOptimization = async (locationName: string) => {
    try {
      const encodedLocation = encodeURIComponent(locationName);
      const response = await fetch(`http://localhost:3001/api/google-business/optimize/${encodedLocation}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch optimization data');
      }
      
      const data = await response.json();
      setOptimization(data);
    } catch (err: any) {
      console.error('Error fetching optimization:', err);
    }
  };

  if (loading && !locationDetails) {
    return (
      <Container>
        <Content>
          <LoadingText>Loading Google Business Profile data...</LoadingText>
        </Content>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <ErrorText>{error}</ErrorText>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Button onClick={fetchAccounts}>Retry</Button>
          </div>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <Header>
          <Title>Google Business Profile Manager</Title>
          <Subtitle>Optimize your Market Nest business listing</Subtitle>
        </Header>

        {optimization && (
          <Card
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CardTitle>Profile Optimization Score</CardTitle>
            <ScoreCircle score={optimization.completionScore}>
              <ScoreText>{optimization.completionScore}%</ScoreText>
            </ScoreCircle>
            
            {optimization.recommendations && optimization.recommendations.length > 0 && (
              <RecommendationsList>
                <h4 style={{ color: 'white', marginBottom: '15px' }}>Recommendations</h4>
                {optimization.recommendations.map((rec: Recommendation, index: number) => (
                  <RecommendationItem key={index} type={rec.type}>
                    <RecommendationType color={rec.type}>{rec.type} Priority</RecommendationType>
                    <RecommendationMessage>{rec.message}</RecommendationMessage>
                    <RecommendationAction>✓ {rec.action}</RecommendationAction>
                  </RecommendationItem>
                ))}
              </RecommendationsList>
            )}
          </Card>
        )}

        <DashboardGrid>
          {locationDetails && (
            <>
              <Card
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <CardTitle>Business Information</CardTitle>
                <InfoRow>
                  <Label>Business Name:</Label>
                  <Value>{locationDetails.title || 'Not set'}</Value>
                </InfoRow>
                <InfoRow>
                  <Label>Address:</Label>
                  <Value>
                    {locationDetails.storefrontAddress?.addressLines?.[0] || 'Not set'}
                  </Value>
                </InfoRow>
                <InfoRow>
                  <Label>Phone:</Label>
                  <Value>
                    {locationDetails.phoneNumbers?.[0]?.phoneNumber || 'Not set'}
                  </Value>
                </InfoRow>
                <InfoRow>
                  <Label>Website:</Label>
                  <Value>{locationDetails.websiteUri || 'Not set'}</Value>
                </InfoRow>
              </Card>

              <Card
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <CardTitle>Categories</CardTitle>
                <InfoRow>
                  <Label>Primary:</Label>
                  <Value>
                    {locationDetails.categories?.primaryCategory?.displayName || 'Not set'}
                  </Value>
                </InfoRow>
                {locationDetails.categories?.additionalCategories?.length > 0 && (
                  <InfoRow>
                    <Label>Additional:</Label>
                    <Value>
                      {locationDetails.categories.additionalCategories.length} categories
                    </Value>
                  </InfoRow>
                )}
              </Card>

              <Card
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <CardTitle>Business Description</CardTitle>
                <p style={{ color: '#a0aec0', lineHeight: '1.6' }}>
                  {locationDetails.profile?.description || 'No description set'}
                </p>
                {locationDetails.profile?.description && (
                  <InfoRow style={{ marginTop: '15px' }}>
                    <Label>Character Count:</Label>
                    <Value>
                      {locationDetails.profile.description.length} / 750
                    </Value>
                  </InfoRow>
                )}
              </Card>
            </>
          )}
        </DashboardGrid>

        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <Button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => selectedLocation && fetchOptimization(selectedLocation)}
          >
            Refresh Analysis
          </Button>
        </div>
      </Content>
    </Container>
  );
};

export default GoogleBusinessManager;
